/**
 * React hooks for PostHog feature flags
 */

import { useState, useEffect } from 'react';
import { usePosthog } from './usePosthog';
import { featureFlagManager } from '../services/posthog';

/**
 * Hook to check if a feature flag is enabled
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlag = (flagKey, defaultValue = false) => {
  const posthogService = usePosthog();
  const [isEnabled, setIsEnabled] = useState(
    featureFlagManager.getFeatureFlag(flagKey, defaultValue),
  );

  useEffect(() => {
    featureFlagManager.initialize(posthogService);

    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled) => {
      setIsEnabled(enabled);
    });

    featureFlagManager.reloadFeatureFlags();

    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return isEnabled;
};

/**
 * Hook to get feature flag with payload
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlagWithPayload = (flagKey, defaultValue = false) => {
  const posthogService = usePosthog();
  const [state, setState] = useState({
    enabled: featureFlagManager.getFeatureFlag(flagKey, defaultValue),
    payload: featureFlagManager.getFeatureFlagPayload(flagKey) || null,
  });

  useEffect(() => {
    featureFlagManager.initialize(posthogService);

    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled, payload) => {
      setState({ enabled, payload: payload || null });
    });

    featureFlagManager.reloadFeatureFlags();

    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return state;
};
